import { InstituteDetails, User } from '@admin/api/apiTypes';
import { useActionQueueCheckQuery, useDashExportMutation, useDashQuery } from '@admin/hooks/useDash';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo } from 'react';
import StyledDivider from '../../../common/components/misc/StyledDivider';
import { useInstitute, useInstituteQuery } from '../../hooks/useInstitute';
import EducatorsTable from './EducatorsTable';
import ExternalCurrencyWidget from './ExternalCurrencyWidget';
import ExportDashboardModal from './modals/ExportDashboardModal';
import OverviewFilter, { INITIAL_FILTERS, OverviewFilters, toPassportTableFilters } from './OverviewFilter';
import AdditionalRequirementsWidget from './overviewWidgets/AdditionalRequirementsWidget';
import EducatorPassportWidget from './overviewWidgets/EducatorPassportWidget';
import EducatorsWidget from './overviewWidgets/EducatorsWidget';
import ProfessionalCurrencyWidget from './overviewWidgets/ProfessionalCurrencyWidget';
import ProfileCompletionWidget from './overviewWidgets/ProfileCompletionWidget';
import OverviewProgramRegistrationsWidget from './overviewWidgets/ProgramRegistrationsWidget';
import OverviewVcmWidget from './overviewWidgets/VcmWidget';
import VocationalCurrencyWidget from './overviewWidgets/VocationalCurrencyWidget';
import { expandRowWidths, packWidgetRows } from './overviewWidgets/WidgetPrimitives';
import ProfilesWidget from './ProfilesWidget';
import ProgramRegistrationsWidget from './ProgramRegistrationsWidget';
import {
  SegmentFilter,
  SegmentFilterContext,
  segmentFilterKey,
  toSegmentRansackParams,
  toggleSegmentFilter
} from './segmentFilters';
import VcmWidget from './VcmWidget';
import YourPeopleWidget from './YourPeopleWidget';

interface OverviewWidgetData {
  type: string;
  [key: string]: unknown;
}

// Base widget widths out of a 12-column grid. Widgets are packed onto rows by
// these widths (see packWidgetRows) and may expand up to 2 extra columns each
// to fill a row (see expandRowWidths), but only when doing so wouldn't
// stretch them too thin.
export const WIDGET_SIZE = {
  small: 2,
  medium: 3,
  large: 4
};

// Maps a Dash widget `type` to the component that renders it and its base
// width (see WIDGET_SIZE) when laid out alongside other widgets in its row.
const OVERVIEW_WIDGETS: Record<
  string,
  { Component: React.ComponentType<{ data: any }>; size: number }
> = {
  educators: { Component: EducatorsWidget, size: WIDGET_SIZE.small },
  educator_passport: { Component: EducatorPassportWidget, size: WIDGET_SIZE.medium },
  vcm: { Component: OverviewVcmWidget, size: WIDGET_SIZE.large },
  additional_requirement_1: { Component: AdditionalRequirementsWidget, size: WIDGET_SIZE.medium },
  additional_requirement_2: { Component: AdditionalRequirementsWidget, size: WIDGET_SIZE.medium },
  profile_completion: { Component: ProfileCompletionWidget, size: WIDGET_SIZE.medium },
  registrations: { Component: OverviewProgramRegistrationsWidget, size: WIDGET_SIZE.medium },
  professional_currency: { Component: ProfessionalCurrencyWidget, size: WIDGET_SIZE.medium },
  vocational_currency: { Component: VocationalCurrencyWidget, size: WIDGET_SIZE.medium }
};

declare module '@mui/material/styles' {
  interface TypographyVariants {
    color: { primary: string };
  }
  interface TypographyVariantsOptions {
    color?: { primary: string };
  }
}

interface Supervisor {
  id?: number;
  name?: string;
}

type TabValue = 'overview' | 'action_queue';

interface HomeProps {
  user: User;
  institute: any; // This is different to InstituteDetails
}

const Home = ({ user, institute }: HomeProps) => {
  const theme = useTheme();
  const [supervisor, setSupervisor] = React.useState<Supervisor>({});
  const [tabValue, setTabValue] = React.useState<TabValue>('overview');
  const [exportModalOpen, setExportModalOpen] = React.useState(false);
  const [exportQueued, setExportQueued] = React.useState(false);
  const [segmentFilters, setSegmentFilters] = React.useState<SegmentFilter[]>([]);

  const [filterDraft, setFilterDraft] = React.useState<OverviewFilters>(INITIAL_FILTERS);
  const [filters, setFilters] = React.useState<OverviewFilters>(INITIAL_FILTERS);
  const { data: dash, isLoading: dashLoading } = useDashQuery(filters);
  const { data: actionQueueCheck } = useActionQueueCheckQuery();
  const passportTableFilters = useMemo(() => toPassportTableFilters(filters), [filters]);
  const exportDash = useDashExportMutation();

  const overviewWidgets = (dash?.widgets ?? []) as OverviewWidgetData[];
  const knownOverviewWidgets = useMemo(
    () => overviewWidgets.filter((item) => OVERVIEW_WIDGETS[item.type]),
    [overviewWidgets]
  );
  const overviewWidgetRows = useMemo(
    () =>
      packWidgetRows(knownOverviewWidgets, (item) => OVERVIEW_WIDGETS[item.type].size),
    [knownOverviewWidgets]
  );

  const { instituteId } = useInstitute();
  const { data: instituteDetails, isLoading: loading } = useInstituteQuery(
    instituteId
  ) as { data: InstituteDetails | undefined; isLoading: boolean };

  const handleTabChange = (event: React.SyntheticEvent, newValue: TabValue) => {
    setTabValue(newValue);
  };

  const handleFilterChange = (newFilters: OverviewFilters) => {
    setFilters(newFilters);
  };

  // Reaches the widgets' SegmentItems through context rather than each widget's
  // props, since widgets are rendered generically from the dashboard payload.
  const segmentFilterContext = useMemo(
    () => ({
      activeKeys: new Set(segmentFilters.map(segmentFilterKey)),
      toggle: (filter: SegmentFilter) =>
        setSegmentFilters((current) => toggleSegmentFilter(current, filter))
    }),
    [segmentFilters]
  );

  const removeSegmentFilter = (filter: SegmentFilter) => {
    const key = segmentFilterKey(filter);
    setSegmentFilters((current) => current.filter((active) => segmentFilterKey(active) !== key));
  };

  const clearSegmentFilters = () => {
    setSegmentFilters([]);
  };

  const openExportModal = () => {
    exportDash.reset();
    setExportModalOpen(true);
  };

  const closeExportModal = () => {
    setExportModalOpen(false);
  };

  // The export is emailed by a background job, so a confirmation snackbar is
  // the only feedback there is to give. The modal promises the files reflect
  // the current filters, so the active segments go along with them.
  const handleExportConfirm = () => {
    const exportFilters = {
      ...passportTableFilters,
      ...toSegmentRansackParams(segmentFilters)
    };

    exportDash.mutate({ q: exportFilters, year: filters.year }, {
      onSuccess: () => {
        setExportModalOpen(false);
        setExportQueued(true);
      }
    });
  };

  useEffect(() => {
    if (
      user.is_any_supervisor &&
      !(user.is_any_admin || user.is_institute_admin)
    ) {
      setSupervisor({
        id: user.id,
        name: user.given_name + ' ' + user.family_name
      });
      setTabValue('action_queue');
    }
  }, [user]);

  const vcmEnabled = useMemo(() => {
    return instituteDetails?.vcm_enabled;
  }, [instituteDetails, user]);

  const programRegistrationsEnabled = useMemo(() => {
    return (
      instituteDetails?.elective_registrations_require_approval ||
      instituteDetails?.learning_journey_registrations_require_approval ||
      instituteDetails?.vocational_elective_registrations_require_approval ||
      instituteDetails?.vocational_learning_journey_registrations_require_approval
    );
  }, [instituteDetails]);

  const externalCurrencyEnabled = useMemo(() => {
    return (
      instituteDetails?.allow_supervisor_to_action &&
      instituteDetails.allow_supervisor_to_action.length > 0
    );
  }, [instituteDetails]);

  const widgetGridSize = useMemo(() => {
    let visibleWidgetCount = 1; // Profiles widget is always visible
    if (vcmEnabled) visibleWidgetCount += 1;
    if (programRegistrationsEnabled) visibleWidgetCount += 1;
    if (externalCurrencyEnabled) visibleWidgetCount += 1;
    return 12 / visibleWidgetCount;
  }, [instituteDetails]);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: theme.typography.pxToRem(8),
            marginBottom: theme.typography.pxToRem(8)
          }}
        >
          <Typography variant="h5">Dashboard</Typography>
          {tabValue === 'overview' ? (
            <Button
              color="secondary"
              onClick={openExportModal}
              size="small"
              variant="contained"
            >
              <Typography variant="button">Export</Typography>
            </Button>
          ) : null}
        </Box>
        <Tabs
          onChange={handleTabChange}
          textColor="secondary"
          indicatorColor="secondary"
          value={tabValue}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            sx={{
              color: theme.typography.color.primary,
              opacity: 0.55,
              '&.Mui-selected': {
                opacity: 1
              }
            }}
            label={
              <Typography
                style={{
                  color: 'inherit',
                  fontSize: theme.typography.pxToRem(15),
                  fontWeight: 500,
                  textTransform: 'capitalize'
                }}
              >
                Overview
              </Typography>
            }
            value="overview"
          />
          <Tab
            sx={{
              color: theme.typography.color.primary,
              opacity: 0.55,
              '&.Mui-selected': {
                opacity: 1
              }
            }}
            label={
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
                <Typography
                  style={{
                    color: 'inherit',
                    fontSize: theme.typography.pxToRem(15),
                    fontWeight: 500,
                    textTransform: 'capitalize'
                  }}
                >
                  Action Queue
                </Typography>
                {/* Flags that at least one panel of the queue has something waiting. */}
                {actionQueueCheck?.unactioned ? (
                  <Box
                    aria-label="You have items to action"
                    component="span"
                    sx={{
                      backgroundColor: theme.palette.error.main,
                      borderRadius: '50%',
                      flexShrink: 0,
                      height: theme.typography.pxToRem(8),
                      width: theme.typography.pxToRem(8)
                    }}
                  />
                ) : null}
              </Box>
            }
            value="action_queue"
          />
        </Tabs>
        <StyledDivider />
      </Grid>

      {loading ? (
        <span>Loading...</span>
      ) : (
        <Grid size={12} container spacing={3}>
          {tabValue === 'overview' && (
            <>
              <Grid size={12} >
                <OverviewFilter
                  filters={filterDraft}
                  onChange={setFilterDraft}
                  onFilter={handleFilterChange}
                />
              </Grid>
              <Grid container spacing={3}>
                {dashLoading ? (
                  <Grid size={12}>
                    <span>Loading...</span>
                  </Grid>
                ) : (
                  <SegmentFilterContext.Provider value={segmentFilterContext}>
                    {overviewWidgetRows.map((row, rowIndex) => {
                      const rowSizes = expandRowWidths(
                        row.map((item) => OVERVIEW_WIDGETS[item.type].size)
                      );
                      return (
                        <Grid key={rowIndex} size={12} container spacing={2}>
                          {row.map((item, itemIndex) => {
                            const { Component } = OVERVIEW_WIDGETS[item.type];
                            return (
                              <Grid key={`${item.type}-${itemIndex}`} size={rowSizes[itemIndex]}>
                                <Component data={item} />
                              </Grid>
                            );
                          })}
                        </Grid>
                      );
                    })}
                  </SegmentFilterContext.Provider>
                )}
              </Grid>

              <Grid size={12}>
                <EducatorsTable
                  user={user}
                  institute={instituteDetails}
                  filters={passportTableFilters}
                  segmentFilters={segmentFilters}
                  onRemoveSegmentFilter={removeSegmentFilter}
                  onClearSegmentFilters={clearSegmentFilters}
                />
              </Grid>
            </>
          )}
          {tabValue === 'action_queue' && (
            <Grid container spacing={2}>
              <Grid size={12} container spacing={2}>
                {vcmEnabled ? (
                  <Grid size={widgetGridSize}>
                    <VcmWidget institute={instituteDetails} />
                  </Grid>
                ) : null}
                <Grid size={widgetGridSize}>
                  <ProfilesWidget institute={instituteDetails} />
                </Grid>
                {programRegistrationsEnabled ? (
                  <Grid size={widgetGridSize}>
                    <ProgramRegistrationsWidget institute={instituteDetails} />
                  </Grid>
                ) : null}
                {externalCurrencyEnabled ? (
                  <Grid size={widgetGridSize}>
                    <ExternalCurrencyWidget
                      user={user}
                      institute={instituteDetails}
                    />
                  </Grid>
                ) : null}
              </Grid>
              <Grid size={12}>
                <YourPeopleWidget
                  user={user}
                  institute={instituteDetails}
                  title={user.is_any_admin || user.is_institute_admin ? "Educators" : "Your People"} />
              </Grid>
            </Grid>
          )}
        </Grid>
      )}

      <ExportDashboardModal
        open={exportModalOpen}
        onClose={closeExportModal}
        onConfirm={handleExportConfirm}
        isPending={exportDash.isPending}
        isError={exportDash.isError}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={6000}
        message="Your export is on its way and will be emailed to you shortly."
        onClose={() => setExportQueued(false)}
        open={exportQueued}
      />
    </Grid>
  );
};

export default Home;
